import { useFormElementDatasourceData } from '@form-engine/core-application/hooks/use-datasource';
import { useFormContext } from '@form-engine/core-application/hooks/use-form';
import {
    useFormElementSession,
    useFormSessionLocals,
} from '@form-engine/core-application/hooks/use-formsession';
import { compile } from '@form-engine/core-util/template';
import { useCallback, useMemo } from 'react';
import { useFormEngineElement } from '@form-engine/core-application/hooks/use-element';
import { useFormEngineElementDisabled } from '@form-engine/core-application/hooks/use-element-disabled';
import { DatasourceResponseOptions } from '@form-engine/core-application/types/form';
import {
    FormCheckListRenderer,
    FormCheckListRendererProps,
} from '../form-check-list';
import { apiName } from '../checkbox-element';

export default function NestedFormCheckList() {
    const {
        state: { showValidFeedback = true },
    } = useFormContext();

    const { state: element } = useFormEngineElement();

    if (!element || element.type !== apiName)
        throw new Error('tried to render checkbox with invalid definition');

    const {
        nestedGroupLabel = '{{$i.Label}}',
        nestedGroupOptions = 'Options',
    } = element;

    const { data } = useFormElementDatasourceData();

    const locals = useFormSessionLocals()?.state ?? {};

    const [session, setSession] = useFormElementSession();

    const disabled = useFormEngineElementDisabled();

    const groups = useMemo(() => {
        const result: {
            label: string;
            options: DatasourceResponseOptions | string[][];
        }[] = [];

        if (!Array.isArray(data)) {
            return result;
        }

        for (const group of data) {
            if (group === null || typeof group !== 'object') {
                return;
            }

            result.push({
                label: compile(String(nestedGroupLabel), {
                    ...locals,
                    $i: group,
                }),
                options: (String(nestedGroupOptions) in group
                    ? group[String(nestedGroupOptions) as keyof typeof group]
                    : []) as DatasourceResponseOptions | string[][],
            });
        }

        return result;
    }, [data, nestedGroupLabel, nestedGroupOptions, locals]);

    const currentValue = useMemo(() => {
        try {
            const value = JSON.parse(String(session?.value || '[]'));
            if (!Array.isArray(value)) {
                return [];
            }
            return value;
        } catch {
            return [];
        }
    }, [session.value]);

    const getChangeHandler = useCallback(
        (index: number) => {
            return (e: React.ChangeEvent) => {
                const target = e.target as HTMLInputElement;
                const newValue: typeof currentValue = Array.from(
                    { length: groups!.length },
                    (_, i) => {
                        return Array.isArray(currentValue[i])
                            ? [...currentValue[i]]
                            : [];
                    }
                );
                const currentGroupValue = newValue.at(index);

                let dirty = false;

                if (
                    target.checked &&
                    !currentGroupValue.includes(target.value)
                ) {
                    dirty = true;
                    currentGroupValue.push(target.value);
                } else if (
                    !target.checked &&
                    currentGroupValue.includes(target.value)
                ) {
                    dirty = true;
                    currentGroupValue.splice(
                        currentGroupValue.indexOf(target.value),
                        1
                    );
                }

                dirty &&
                    setSession({
                        ...session,
                        isTouched: true,
                        value: JSON.stringify(newValue),
                    });
            };
        },
        [groups, currentValue, session, setSession]
    );

    return (
        <div className="fe-nested-checkbox-list">
            {element.label && <p className="form-label">{element.label}</p>}
            <ul className="fe-nested-checkbox-list--items">
                {groups!.map((groupData, index) => {
                    const props: FormCheckListRendererProps = {
                        inline: Boolean(element.inline),
                        disabled,
                        isTouched: session.isTouched,
                        showError: Boolean(element.showError),
                        showValidFeedback,
                        label: groupData.label,
                        error: session.error || undefined,
                        locals: { ...locals, $i: (data as unknown[])[index] },
                        options: groupData.options,
                        currentValue: currentValue.at(index) ?? [],
                        name: String(element.name),
                        onChange: getChangeHandler(index),
                    };

                    return (
                        <li key={index}>
                            <FormCheckListRenderer {...props} />
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
